(() => {
  const drawer = document.querySelector('[data-cart-drawer].store-cart');
  if (!drawer) return;

  const moneyFormat = window.moneyFormat || '${{amount}}';
  const productCache = new Map();
  let syncTimer;
  let recommendationProductId;
  let rulesCartSignature;

  const protectRoot = drawer.querySelector('[data-shipping-protect]');
  const protectProductId = protectRoot ? Number(protectRoot.getAttribute('data-protect-product-id')) : 0;

  const isProtectionProduct = (product) =>
    String(product?.handle || '').startsWith('shipping-protection') ||
    Number(product?.id) === protectProductId;

  const stripProtect = (cart) => {
    if (!protectProductId || !cart || !Array.isArray(cart.items)) return cart;
    const kept = cart.items.filter((item) => Number(item.product_id) !== protectProductId);
    if (kept.length === cart.items.length) return cart;
    return Object.assign({}, cart, {
      items: kept,
      item_count: kept.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    });
  };

  const escapeHTML = (value = '') =>
    value
      .toString()
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount_no_decimals}}', Math.round(Number(amount)).toString())
      .replace('{{ amount_no_decimals }}', Math.round(Number(amount)).toString());
  };

  const fetchCart = async () => {
    const response = await fetch(window.themeRoutes.cart, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    return stripProtect(cart);
  };

  const fetchProduct = async (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    productCache.set(handle, request);
    return request;
  };

  const getReferencePrice = async (item) => {
    const product = await fetchProduct(item.handle);
    const variant = product?.variants?.find((candidate) => Number(candidate.id) === Number(item.variant_id));
    return Math.max(
      Number(item.original_price || item.final_price || 0),
      Number(variant?.compare_at_price || 0)
    );
  };

  const updateShipping = (cart) => {
    const shipping = drawer.querySelector('[data-free-shipping-threshold]');
    if (!shipping) return;

    const threshold = Number(shipping.dataset.freeShippingThreshold || 0);
    const message = shipping.querySelector('[data-shipping-message]');
    const progress = shipping.querySelector('.progress span');
    const percent = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100;
    const progressText =
      shipping.dataset.shippingProgressText || 'Spend [amount] more and get free shipping.';
    const unlockedText = shipping.dataset.shippingUnlockedText || 'You unlocked free shipping.';

    if (message) {
      message.textContent =
        threshold > 0 && cart.total_price < threshold
          ? progressText.replace('[amount]', formatMoney(threshold - cart.total_price))
          : unlockedText;
    }
    if (progress) progress.style.width = `${percent}%`;
  };

  const updateCounts = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });
    const drawerCount = drawer.querySelector('[data-cart-drawer-count]');
    if (drawerCount) drawerCount.textContent = cart.item_count;
  };

  const updatePrices = async (cart) => {
    const references = await Promise.all(cart.items.map(getReferencePrice));
    let compareTotal = 0;
    let itemsLineTotal = 0;

    cart.items.forEach((item, index) => {
      const referenceLine = references[index] * item.quantity;
      compareTotal += Math.max(referenceLine, item.final_line_price);
      itemsLineTotal += Number(item.final_line_price || 0);

      const cartItem = drawer.querySelectorAll('.cart-item')[index];
      if (!cartItem) return;

      let price = cartItem.querySelector('[data-cart-item-price]');
      if (!price) {
        price = document.createElement('div');
        price.className = 'cart-item__price';
        price.dataset.cartItemPrice = '';
        const meta = cartItem.querySelector('.cart-item__meta');
        const quantity = cartItem.querySelector('.quantity');
        if (meta) meta.before(price);
        else if (quantity) quantity.before(price);
        else cartItem.querySelector('.cart-item__details')?.append(price);
      }

      price.innerHTML = `
        ${referenceLine > item.final_line_price ? `<s>${formatMoney(referenceLine)}</s>` : ''}
        <strong>${formatMoney(item.final_line_price)}</strong>
      `;
    });

    const savings = Math.max(0, compareTotal - itemsLineTotal);
    const compareNode = drawer.querySelector('[data-cart-compare-total]');
    const subtotalNode = drawer.querySelector('[data-cart-subtotal]');
    const savingsNode = drawer.querySelector('[data-cart-savings]');

    if (subtotalNode) subtotalNode.textContent = formatMoney(cart.total_price);
    if (compareNode) {
      compareNode.textContent = formatMoney(compareTotal);
      compareNode.hidden = savings <= 0;
    }
    if (savingsNode) {
      savingsNode.textContent = `−${formatMoney(savings)}`;
      savingsNode.hidden = savings <= 0;
    }
  };

  const renderItems = (cart) => {
    const container = drawer.querySelector('[data-cart-items]');
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = `
        <div class="empty-state store-cart__empty">
          <p>Your cart is empty.</p>
          <a class="button" href="/collections">Continue shopping</a>
        </div>
      `;
      return;
    }

    container.innerHTML = cart.items
      .map((item, index) => {
        const image = item.image
          ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.product_title)}" width="120" height="160" loading="lazy">`
          : '';
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-item__variant">${escapeHTML(item.variant_title)}</p>`
          : '';

        return `
          <div class="cart-item store-cart-item" data-line="${index + 1}" data-cart-item-key="${escapeHTML(item.key)}">
            <a class="cart-item__image" href="${escapeHTML(item.url)}">${image}</a>
            <div class="cart-item__details">
              <a class="cart-item__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
              ${variant}
              <div class="cart-item__price" data-cart-item-price>
                <strong>${formatMoney(item.final_line_price)}</strong>
              </div>
              <quantity-input class="quantity">
                <button type="button" data-quantity-minus aria-label="Decrease quantity">−</button>
                <input type="number" min="0" value="${item.quantity}" aria-label="Quantity for ${escapeHTML(item.product_title)}" data-quantity-input>
                <button type="button" data-quantity-plus aria-label="Increase quantity">+</button>
              </quantity-input>
            </div>
          </div>
        `;
      })
      .join('');
  };

  const refreshUpsellRules = async (cart) => {
    const signature = cart.items.map((item) => item.product_id).join(',');
    if (signature === rulesCartSignature) return;

    const sectionWrapper = drawer.closest('.shopify-section');
    const sectionId = sectionWrapper ? sectionWrapper.id.replace('shopify-section-', '') : 'cart-drawer';
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' }
    }).catch(() => null);
    if (!response?.ok) return;

    const sections = await response.json().catch(() => null);
    const html = sections?.[sectionId];
    if (!html) return;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const freshRules = documentFragment.querySelector('[data-cart-upsell-rules]');
    const currentRules = drawer.querySelector('[data-cart-upsell-rules]');
    if (freshRules && currentRules) {
      currentRules.innerHTML = freshRules.innerHTML;
      recommendationProductId = null;
    }
    rulesCartSignature = signature;
  };

  const updateUpsellDots = (track) => {
    const dots = track.parentElement?.querySelector('[data-cart-upsell-dots]');
    const cards = Array.from(track.children);
    if (!dots || cards.length < 2) return;

    const center = track.scrollLeft + track.clientWidth / 2;
    let activeIndex = 0;
    let nearestDistance = Infinity;

    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const distance = Math.abs(cardCenter - center);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        activeIndex = index;
      }
    });

    dots.querySelectorAll('button').forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  };

  const renderUpsellDots = (track) => {
    let dots = track.parentElement?.querySelector('[data-cart-upsell-dots]');
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'store-cart-upsell__dots';
      dots.dataset.cartUpsellDots = '';
      dots.setAttribute('aria-label', 'Product recommendations pagination');
      track.after(dots);
    }

    const cards = Array.from(track.children);
    dots.innerHTML = cards
      .map(
        (_, index) =>
          `<button type="button" class="${index === 0 ? 'is-active' : ''}" aria-label="Show recommendation ${index + 1}" data-cart-upsell-dot="${index}"></button>`
      )
      .join('');
    dots.hidden = cards.length < 2;

    dots.querySelectorAll('[data-cart-upsell-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const card = cards[Number(dot.dataset.cartUpsellDot)];
        if (card) {
          track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        }
      });
    });

    if (track.dataset.cartUpsellDotsReady !== 'true') {
      let ticking = false;
      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            updateUpsellDots(track);
            ticking = false;
          });
        },
        { passive: true }
      );
      track.dataset.cartUpsellDotsReady = 'true';
    }

    updateUpsellDots(track);
  };

  const renderRecommendations = async (cart) => {
    const wrapper = drawer.querySelector('[data-cart-upsell]');
    const track = drawer.querySelector('[data-cart-upsell-track]');
    const heading = wrapper?.querySelector('h2');
    if (!wrapper || !track || drawer.dataset.cartRecommendations !== 'true' || cart.item_count === 0) {
      if (wrapper) wrapper.hidden = true;
      return;
    }

    const rules = Array.from(drawer.querySelectorAll('[data-cart-upsell-rule]'));
    let manualRule = null;
    let manualProductId = null;
    for (const item of cart.items) {
      const candidate = rules.find((rule) => Number(rule.dataset.mainProductId) === Number(item.product_id));
      if (candidate) {
        manualRule = candidate;
        manualProductId = Number(item.product_id);
        break;
      }
    }

    const productId = manualProductId || cart.items[0]?.product_id;
    if (!productId) {
      wrapper.hidden = true;
      return;
    }

    const sourceKey = manualRule ? `manual-${productId}-${manualRule.textContent.length}` : `automatic-${productId}`;

    if (recommendationProductId === sourceKey && track.childElementCount > 0) {
      wrapper.hidden = false;
      return;
    }

    let products = [];

    if (manualRule) {
      try {
        products = JSON.parse(manualRule.textContent);
        if (heading) {
          heading.textContent =
            manualRule.dataset.ruleHeading || wrapper.dataset.cartUpsellDefaultHeading || 'PEOPLE ALSO BOUGHT';
        }
      } catch (error) {
        products = [];
      }
    } else if (drawer.dataset.cartAutomaticFallback === 'true') {
      if (heading) {
        heading.textContent = wrapper.dataset.cartUpsellDefaultHeading || 'PEOPLE ALSO BOUGHT';
      }
      const limit = Number(drawer.dataset.cartRecommendationsLimit || 4);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`,
        { headers: { Accept: 'application/json' } }
      ).catch(() => null);

      if (response?.ok) {
        const data = await response.json();
        products = data.products || [];
      }
    }

    const cartProductIds = new Set(cart.items.map((item) => Number(item.product_id)));
    products = products.filter(
      (product) =>
        product.available &&
        !cartProductIds.has(Number(product.id)) &&
        !isProtectionProduct(product)
    );

    if (products.length === 0) {
      wrapper.hidden = true;
      return;
    }

    track.innerHTML = products
      .map((product) => {
        const variant = product.variants?.find((candidate) => candidate.available);
        if (!variant) return '';
        const compareAt = Number(variant.compare_at_price || product.compare_at_price || 0);
        const price = Number(variant.price || product.price || 0);
        const image = product.featured_image
          ? `<img src="${escapeHTML(product.featured_image)}" alt="${escapeHTML(product.title)}" width="180" height="225" loading="lazy">`
          : '';

        const action = `
          <button
            class="store-cart-upsell__add"
            type="button"
            data-quick-add-open="${escapeHTML(product.handle)}"
          >
            ADD +
          </button>
        `;

        return `
          <article class="store-cart-upsell__card">
            <a class="store-cart-upsell__image" href="${escapeHTML(product.url)}">${image}</a>
            <div class="store-cart-upsell__info">
              <a class="store-cart-upsell__title" href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a>
              <div class="store-cart-upsell__price">
                ${compareAt > price ? `<s>${formatMoney(compareAt)}</s>` : ''}
                <strong>${formatMoney(price)}</strong>
              </div>
              ${action}
            </div>
          </article>
        `;
      })
      .join('');

    recommendationProductId = sourceKey;
    wrapper.hidden = track.childElementCount === 0;
    renderUpsellDots(track);
  };

  const syncCart = async (providedCart) => {
    const cart = stripProtect(providedCart) || await fetchCart();
    updateCounts(cart);
    updateShipping(cart);
    await updatePrices(cart);
    await refreshUpsellRules(cart);
    await renderRecommendations(cart);
    return cart;
  };

  const scheduleSync = () => {
    clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      syncCart().catch(() => {});
    }, 120);
  };

  const items = drawer.querySelector('[data-cart-items]');
  if (items) {
    new MutationObserver(scheduleSync).observe(items, { childList: true });
  }

  document.addEventListener('shopify:section:load', () => {
    recommendationProductId = null;
    rulesCartSignature = null;
    scheduleSync();
  });

  drawer.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button) return;

    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'ADDING';

    try {
      const response = await fetch(window.themeRoutes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.cartUpsellAdd), quantity: 1 }]
        })
      });
      if (!response.ok) throw new Error('Unable to add recommendation');

      const cart = await fetchCart();
      recommendationProductId = null;
      renderItems(cart);
      await syncCart(cart);
    } catch (error) {
      button.textContent = 'TRY AGAIN';
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 1400);
      return;
    }

    button.disabled = false;
    button.textContent = originalLabel;
  });

  document.addEventListener('store:cart-updated', (event) => {
    const cart = stripProtect(event.detail?.cart);
    if (!cart) return;

    recommendationProductId = null;
    renderItems(cart);
    syncCart(cart).catch(() => {});
  });

  const timerNode = drawer.querySelector('[data-cart-timer-value]');
  if (timerNode) {
    const duration = Math.max(1, Number(drawer.dataset.cartTimerMinutes || 10)) * 60 * 1000;
    const storageKey = 'storeCartCheckoutTimer';
    let expiresAt = Number(sessionStorage.getItem(storageKey) || 0);
    const now = Date.now();

    if (!expiresAt || expiresAt <= now) {
      expiresAt = now + duration;
      sessionStorage.setItem(storageKey, expiresAt.toString());
    }

    const updateTimer = () => {
      let remaining = Math.max(0, expiresAt - Date.now());
      if (remaining === 0) {
        expiresAt = Date.now() + duration;
        sessionStorage.setItem(storageKey, expiresAt.toString());
        remaining = duration;
      }
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      timerNode.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    updateTimer();
    window.setInterval(updateTimer, 1000);
  }

  syncCart().catch(() => {});
})();
